import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, LogOut, Inbox, Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSwitch } from "@/components/Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "nhn_admin_token";
const LOCALES = { da: "da-DK", en: "en-GB", sw: "sw-TZ" };

const formatError = (detail, fallback) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return fallback;
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [inquiries, setInquiries] = useState(null);
  const [sendingSheet, setSendingSheet] = useState(false);
  const { t, lang } = useLanguage();

  const sendSheet = async () => {
    setSendingSheet(true);
    try {
      await axios.post(`${API}/admin/send-proofreading`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("admin.proofOk"));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail, t("admin.error")));
    } finally {
      setSendingSheet(false);
    }
  };

  const fetchInquiries = useCallback(
    async (t) => {
      try {
        const { data } = await axios.get(`${API}/admin/inquiries`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setInquiries(data);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }
    },
    []
  );

  useEffect(() => {
    if (token) fetchInquiries(token);
  }, [token, fetchInquiries]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success(t("admin.welcome"));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail, t("admin.error")));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setInquiries(null);
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#050505] px-6">
        <div className="noise-overlay" aria-hidden="true" />
        <LanguageSwitch />
        <Card className="w-full max-w-md border-white/10 bg-[#0A0A0A]">
          <CardHeader>
            <CardTitle className="font-syne text-2xl font-bold tracking-tight text-[#F2F2F2]">
              Admin<span className="text-[#FF3333]">.</span>
            </CardTitle>
            <p className="text-sm text-[#A3A3A3]">{t("admin.loginSub")}</p>
          </CardHeader>
          <CardContent>
            <form data-testid="admin-login-form" onSubmit={login} className="flex flex-col gap-4">
              <Input
                data-testid="admin-email-input"
                type="email"
                required
                placeholder={t("admin.emailPh")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-white/15 bg-transparent text-[#F2F2F2]"
              />
              <Input
                data-testid="admin-password-input"
                type="password"
                required
                placeholder={t("admin.passPh")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-white/15 bg-transparent text-[#F2F2F2]"
              />
              <Button
                data-testid="admin-login-button"
                type="submit"
                disabled={loading}
                className="mt-2 bg-[#FF3333] text-white hover:bg-[#FF3333]/90"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("admin.loginBtn")}
              </Button>
              <Link
                data-testid="admin-back-home-link"
                to="/"
                className="mt-2 flex items-center justify-center gap-1.5 text-xs text-[#A3A3A3] transition-colors hover:text-[#F2F2F2]"
              >
                <ArrowLeft className="h-3 w-3" /> {t("admin.backHome")}
              </Link>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] px-6 py-16 md:px-12">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF3333]">{t("admin.label")}</p>
            <h1 className="mt-2 font-syne text-4xl font-extrabold tracking-tighter text-[#F2F2F2]">
              {t("admin.title")}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="admin-send-proofreading-button"
              onClick={sendSheet}
              disabled={sendingSheet}
              variant="outline"
              className="border-white/15 text-[#F2F2F2] hover:bg-white/5"
            >
              {sendingSheet ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
              {t("admin.proofBtn")}
            </Button>
            <LanguageSwitch />
            <Link to="/" data-testid="admin-home-link">
              <Button variant="outline" className="border-white/15 text-[#F2F2F2] hover:bg-white/5">
                <ArrowLeft className="mr-2 h-4 w-4" /> {t("admin.home")}
              </Button>
            </Link>
            <Button
              data-testid="admin-logout-button"
              onClick={logout}
              variant="outline"
              className="border-white/15 text-[#F2F2F2] hover:bg-white/5"
            >
              <LogOut className="mr-2 h-4 w-4" /> {t("admin.logout")}
            </Button>
          </div>
        </div>

        {inquiries === null ? (
          <div className="mt-24 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#FF3333]" />
          </div>
        ) : inquiries.length === 0 ? (
          <div data-testid="admin-empty-state" className="mt-24 flex flex-col items-center gap-4 text-center">
            <Inbox className="h-12 w-12 text-[#555]" />
            <p className="text-[#A3A3A3]">{t("admin.empty")}</p>
          </div>
        ) : (
          <div data-testid="admin-inquiries-list" className="mt-12 flex flex-col gap-4">
            {inquiries.map((inq) => (
              <Card
                key={inq.id}
                data-testid={`admin-inquiry-${inq.id}`}
                className="border-white/10 bg-[#0A0A0A] transition-colors duration-300 hover:border-[#FF3333]/40"
              >
                <CardContent className="flex flex-col gap-3 p-6">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-syne text-lg font-bold text-[#F2F2F2]">{inq.name}</p>
                    <p className="text-xs text-[#A3A3A3]">
                      {new Date(inq.created_at).toLocaleString(LOCALES[lang] || "da-DK")}
                    </p>
                  </div>
                  <a
                    href={`mailto:${inq.email}`}
                    className="flex w-fit items-center gap-2 text-sm text-[#FF3333] hover:underline"
                  >
                    <Mail className="h-3.5 w-3.5" /> {inq.email}
                  </a>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#A3A3A3]">{inq.message}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
